import { InviteDelivery, MemberDto } from "@growth/dto";
import { MemberRole, memberRoleSchema, ROLE_LABELS } from "@growth/schema";
import { Badge, Button, ConfirmDialog, Field, Input } from "@growth/ui";
import { Copy, LoaderCircle, Mail, Trash2, UserPlus } from "lucide-react";
import { type FormEvent, useState } from "react";

import { ErrorText, PageHeader, selectClass } from "../components/page-header";
import { useActive } from "../lib/active";
import { apiErrorMessage } from "../lib/api-error";
import {
  useChangeRole,
  useInviteMember,
  useMembers,
  useRemoveMember,
  useResendInvite,
} from "../lib/workspace";

/** Who can see and change this workspace, and with what role. */
export const MembersPage = () => {
  const { workspace, atLeast } = useActive();
  const isOwner = atLeast("OWNER");
  const members = useMembers();
  const changeRole = useChangeRole();
  const remove = useRemoveMember();
  const actionError = changeRole.error ?? remove.error;

  return (
    <section className="max-w-4xl space-y-8">
      <PageHeader eyebrow={workspace?.name ?? ""} title="Members">
        Everyone here sees every brand in this workspace. Roles decide what they can change.
      </PageHeader>

      <dl className="grid gap-3 sm:grid-cols-2">
        {memberRoleSchema.options.map((role) => (
          <div key={role} className="rounded-lg border border-line bg-white px-4 py-3 text-sm">
            <dt className="font-medium">{ROLE_LABELS[role].label}</dt>
            <dd className="text-muted">{ROLE_LABELS[role].can}</dd>
          </div>
        ))}
      </dl>

      {isOwner && <InviteForm />}
      {actionError && <ErrorText>{apiErrorMessage(actionError)}</ErrorText>}
      {members.isError && <ErrorText>{apiErrorMessage(members.error)}</ErrorText>}

      <div className="overflow-x-auto rounded-xl border border-line bg-white">
        <table className="w-full text-sm">
          <tbody className="divide-y divide-line">
            {members.isPending && (
              <tr>
                <td className="px-4 py-6">
                  <LoaderCircle className="size-5 animate-spin text-muted" aria-label="Loading" />
                </td>
              </tr>
            )}
            {members.data?.map((member) => (
              <MemberRow
                key={member.id}
                member={member}
                editable={isOwner}
                onRole={(role) => changeRole.mutate({ id: member.id, role })}
                onRemove={() => remove.mutate(member.id)}
              />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

const MemberRow = ({
  member,
  editable,
  onRole,
  onRemove,
}: {
  member: MemberDto;
  editable: boolean;
  onRole: (role: MemberRole) => void;
  onRemove: () => void;
}) => (
  <tr>
    <td className="px-4 py-3">
      <p className="text-ink">
        {member.name ?? member.email} {member.isYou && <span className="text-muted">(you)</span>}
      </p>
      {member.name && <p className="text-xs text-muted">{member.email}</p>}
    </td>
    <td className="w-40 px-4 py-3">
      {member.status === "INVITED" ? (
        <div className="flex flex-wrap items-center gap-1">
          <Badge>Invited</Badge>
          {editable && <ResendButton member={member} />}
        </div>
      ) : (
        <Badge tone="success">Active</Badge>
      )}
    </td>
    <td className="w-44 px-4 py-3">
      {editable ? (
        <select
          aria-label={`Role for ${member.email}`}
          className={selectClass}
          value={member.role}
          onChange={(event) => onRole(event.target.value as MemberRole)}
        >
          {memberRoleSchema.options.map((role) => (
            <option key={role} value={role}>
              {ROLE_LABELS[role].label}
            </option>
          ))}
        </select>
      ) : (
        ROLE_LABELS[member.role].label
      )}
    </td>
    {editable && (
      <td className="w-14 px-2 py-2 text-right">
        <ConfirmDialog
          trigger={
            <Button variant="ghost" aria-label={`Remove ${member.email}`}>
              <Trash2 className="size-4" />
            </Button>
          }
          title={member.isYou ? "Leave this workspace?" : `Remove ${member.email}?`}
          description={
            member.isYou
              ? "You'll lose access straight away. Another owner can add you back."
              : "They lose access straight away. Their past work stays."
          }
          confirmLabel={member.isYou ? "Leave" : "Remove"}
          destructive
          onConfirm={onRemove}
        />
      </td>
    )}
  </tr>
);

/** What happened about telling someone, after adding them. */
const DELIVERY_NOTE: Record<InviteDelivery, (email: string) => string> = {
  added: (email) => `${email} is in. They'll see this workspace the next time they open the app.`,
  sent: (email) =>
    `Invite sent to ${email}. The email has a link to make an account, and they're in as soon as they do.`,
  has_account: (email) =>
    `${email} already has an account, so no email went out. Send them the link below; they're in when they sign in.`,
  failed: (email) =>
    `${email} is added, but the invite email didn't go out. Try Resend, or send them the link below.`,
};

/** Short form of the same, beside a pending invite. */
const RESENT_NOTE: Record<InviteDelivery, string> = {
  added: "Joined",
  sent: "Sent",
  has_account: "Has an account: send the link",
  failed: "Didn't send",
};

const ResendButton = ({ member }: { member: MemberDto }) => {
  const resend = useResendInvite();
  if (resend.data)
    return <span className="text-xs text-muted">{RESENT_NOTE[resend.data.delivery]}</span>;
  return (
    <Button
      type="button"
      variant="ghost"
      aria-label={`Resend the invite to ${member.email}`}
      disabled={resend.isPending}
      onClick={() => resend.mutate(member.id)}
    >
      {resend.isPending ? (
        <LoaderCircle className="size-3.5 animate-spin" />
      ) : (
        <Mail className="size-3.5" />
      )}
      <span className="text-xs">{resend.isError ? "Try again" : "Resend"}</span>
    </Button>
  );
};

const InviteForm = () => {
  const invite = useInviteMember();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<MemberRole>("EDITOR");
  const [copied, setCopied] = useState(false);
  const link = window.location.origin;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    invite.mutate({ email, role }, { onSuccess: () => setEmail("") });
  };
  const added = invite.isSuccess ? invite.data : null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3 rounded-xl border border-line bg-white p-5">
      <h2 className="font-serif text-lg text-ink">Add someone</h2>
      <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_11rem_auto] sm:items-end">
        <Field label="Email">
          {(props) => (
            <Input
              {...props}
              type="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          )}
        </Field>
        <Field label="Role">
          {(props) => (
            <select
              {...props}
              className={selectClass}
              value={role}
              onChange={(event) => setRole(event.target.value as MemberRole)}
            >
              {memberRoleSchema.options.map((option) => (
                <option key={option} value={option}>
                  {ROLE_LABELS[option].label}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Button type="submit" disabled={invite.isPending}>
          {invite.isPending ? (
            <LoaderCircle className="size-4 animate-spin" />
          ) : (
            <UserPlus className="size-4" />
          )}
          Add
        </Button>
      </div>
      {added && (
        <p role="status" className="rounded-lg bg-paper px-3 py-2 text-sm text-ink">
          {DELIVERY_NOTE[added.delivery](added.email)}
        </p>
      )}
      <p className="flex flex-wrap items-center gap-2 text-xs text-muted">
        New people get an invite email. You can also send this link; they join when they sign in
        with that email:
        <code className="rounded bg-paper px-1.5 py-0.5 text-ink">{link}</code>
        <Button type="button" variant="ghost" onClick={() => void copy()}>
          <Copy className="size-3.5" />
          {copied ? "Copied" : "Copy"}
        </Button>
      </p>
      {invite.isError && <ErrorText>{apiErrorMessage(invite.error)}</ErrorText>}
    </form>
  );
};
